import { CloseOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useThreadPanelStore } from '../stores';
import { useMobileViewport } from '../hooks/useMobileViewport';
import TaskStatusPill from './TaskStatusPill';
import TaskHeartbeatControl from './TaskHeartbeatControl';
import type { Task } from '../lib/api';
import { ExecutionUsageChip } from './ExecutionUsageChip';

/**
 * The toggle names what it controls and leaves where it currently stands to
 * `aria-pressed`. Naming the action instead — "let the thread float" while
 * pressed, "dock the thread" while not — has a screen reader announce the next
 * placement and the present one in the same breath, which reads as a
 * contradiction.
 */
const PLACEMENT_LABEL = 'Dock the thread beside the channel';

/**
 * The header both thread panels wear: the title on the left, and on the right
 * the placement toggle followed by the only control that closes the panel.
 *
 * The toggle is absent on mobile, where the thread takes the whole screen and
 * has no channel to sit beside or float over.
 */
interface Props {
  title: string;
  onClose: () => void;
  task?: Task;
  assigneeName?: string;
  parentChannelRoute?: string;
  conversationId?: string;
}

export default function ThreadPanelHeader({
  title,
  onClose,
  task,
  assigneeName,
  parentChannelRoute,
  conversationId,
}: Props) {
  const isMobile = useMobileViewport();
  const mode = useThreadPanelStore((s) => s.mode);
  const toggleMode = useThreadPanelStore((s) => s.toggleMode);

  const pinned = mode === 'pinned';

  return (
    <div className="thread-panel-header">
      <div className="thread-panel-heading">
        <span className="thread-panel-title" title={task?.title}>
          {task?.title ?? title}
        </span>
        {task && (
          <div className="thread-panel-task-controls">
            <TaskStatusPill
              task={task}
              assigneeName={assigneeName}
              showUnassigned={!task.assignee_id}
            />
            <TaskHeartbeatControl task={task} />
          </div>
        )}
      </div>
      <div className="thread-panel-actions">
        {parentChannelRoute && (
          <Link
            className="thread-panel-parent-channel"
            to={parentChannelRoute}
            aria-label="Go to channel"
            title="Go to channel"
          >
            Go to channel
          </Link>
        )}
        {conversationId && <ExecutionUsageChip resourceId={conversationId} scope="thread" />}
        {!isMobile && (
          <button
            type="button"
            className="thread-panel-placement"
            onClick={toggleMode}
            aria-pressed={pinned}
            aria-label={PLACEMENT_LABEL}
            title={PLACEMENT_LABEL}
          >
            {pinned ? <PushpinFilled /> : <PushpinOutlined />}
          </button>
        )}
        <button
          type="button"
          className="thread-panel-close"
          onClick={onClose}
          aria-label="Close thread"
          title="Close thread"
        >
          <CloseOutlined />
        </button>
      </div>
    </div>
  );
}
